import { z } from 'zod';

export const ZVendorApplication = z.object({
  // Company Info
  company_name: z.string().min(2, 'Company name is required'),
  company_website: z.string().url('Must be a valid URL'),
  company_year_founded: z.number().min(1900).max(new Date().getFullYear()).optional(),
  company_size: z.string().optional(),
  company_stage: z.string().optional(),
  company_location: z.string().optional(),
  company_linkedin: z.string().url().optional().or(z.literal('')),
  
  // Contact Person
  contact_name: z.string().min(2, 'Contact name is required'),
  contact_title: z.string().min(2, 'Title is required'),
  contact_email: z.string().email('Must be a valid email'),
  contact_phone: z.string().optional(),
  contact_linkedin: z.string().url().optional().or(z.literal('')),
  
  // Product Info
  product_name: z.string().min(2, 'Product name is required'),
  product_category: z.string().min(1, 'Category is required'),
  service_type: z.enum(['product', 'service', 'education']),
  product_brief_description: z.string().min(20, 'Brief description must be at least 20 characters'),
  product_detailed_description: z.string().min(100, 'Detailed description must be at least 100 characters'),
  product_website: z.string().url('Must be a valid URL'),
  product_demo_url: z.string().url().optional().or(z.literal('')),
  target_audience: z.string().min(10, 'Target audience is required'),
  problem_solved: z.string().min(20, 'Problem solved description is required'),
  
  // Pricing
  current_price: z.number().min(0, 'Price must be positive'),
  proposed_deal_price: z.number().min(0, 'Deal price must be positive'),
  pricing_model: z.string().min(5, 'Pricing model is required'),
  plan_count: z.number().optional(),
  deal_includes: z.string().min(10, 'Deal inclusions are required'),
  
  // Assets (URLs after upload)
  logo_url: z.string().optional(),
  screenshots: z.array(z.string().url()).optional(),
  demo_video_url: z.string().url().optional().or(z.literal('')),
  testimonials: z.array(z.object({
    quote: z.string(),
    author: z.string(),
    role: z.string().optional(),
  })).optional(),
  
  // Technical
  key_features: z.array(z.string().min(5)).min(3, 'At least 3 key features required'),
  integrations: z.array(z.string()).optional(),
  technical_requirements: z.string().optional(),
  setup_time_estimate: z.string().optional(),
  
  // Business Metrics
  customer_count: z.number().optional(),
  monthly_revenue: z.number().optional(),
  average_rating: z.number().min(0).max(5).optional(),
  prior_ltd_experience: z.boolean().optional(),
  
  // Deal Objectives
  primary_goal: z.string().min(10, 'Primary goal is required'),
  expected_units: z.number().optional(),
  campaign_duration: z.string().optional(),
  special_requirements: z.string().optional(),
  
  // Legal
  terms_url: z.string().url('Terms URL is required'),
  privacy_url: z.string().url('Privacy URL is required'),
  agreed_to_terms: z.boolean().refine(val => val === true, 'You must agree to the terms'),
  consent_to_processing: z.boolean().refine(val => val === true, 'You must consent to data processing'),
});

export type VendorApplication = z.infer<typeof ZVendorApplication>;

export interface VendorApplicationRow extends VendorApplication {
  id: string;
  created_at: string;
  updated_at: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'needs_info';
  reviewed_at?: string;
  reviewed_by?: string;
  review_notes?: string;
}
